<script lang="ts" setup>
import { ref } from 'vue'

interface PropsClient {
  fallback?: string
  fallbackTag?: string
  classes?: string
}

withDefaults(defineProps<PropsClient>(), {
  fallback: '',
  fallbackTag: '',
  classes: '',
})
const isLoading = ref(true)

onMounted(() => {
  isLoading.value = false
})
</script>

<template>
  <ClientOnly>
    <template #fallback>
      <section
        role="status"
        aria-label="Content is loading"
        aria-busy="true"
        :class="`mx-auto ${fallback === 'banner' ? '' : 'my-4 lg:p-6'} ${classes} filter-50 h-auto max-lg:max-w-[90vw] lg:max-w-[75%] animate-pulse w-full`"
      >
        <div v-if="fallback === 'banner'" class="relative mx-auto max-w-xl rounded-sm shadow-sm">
          <div class="mb-4 h-60 w-full flex items-center justify-center">
            <svg class="h-14 w-14 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 20">
              <path d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z"/>
              <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z"/>
            </svg>
          </div>
        </div>
        <div v-else-if="fallback === 'image'" class="relative mx-auto max-w-md rounded-sm shadow-sm">
          <div class="mb-4 h-60 w-full flex items-center justify-center">
            <svg class="h-14 w-14 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 20">
              <path d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z"/>
              <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z"/>
            </svg>
          </div>
        </div>
        <div v-else-if="fallback === 'card'" class="max-w-xxl relative mx-auto w-full flex-row lg:flex lg:gap-4">
          <div v-for="(el) in 2" :key="el" class="max-w-full w-full rounded-sm shadow-sm">
            <div class="mb-4 h-48 flex items-center justify-center rounded-sm bg-gray-300 dark:bg-gray-700">
              <svg class="h-10 w-10 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 20">
                <path d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z"/>
                <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z"/>
              </svg>
            </div>
            <div class="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
        <div v-else-if="fallback === 'section'" class="mx-auto my-4">
          <div class="mb-6 w-30 rounded-full bg-gray-200 lg:h-3 max-lg:h-2.5 dark:bg-gray-700"></div>
          <div class="lg:flex lg:items-center space-y-8 lg:space-x-8 lg:space-y-0 rtl:space-x-reverse">
            <div class="h-60 w-full flex items-center justify-center rounded-sm bg-gray-300 sm:w-200 dark:bg-gray-700">
              <svg class="h-10 w-10 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 18">
                <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z"/>
              </svg>
            </div>
            <div class="w-full">
              <div class="mb-2.5 h-2 max-w-[80%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="mb-2.5 h-2 max-w-[60%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="mb-2.5 h-2 max-w-[70%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="mb-2.5 h-2 max-w-[50%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
              <div class="h-2 max-w-[40%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            </div>
          </div>
        </div>
        <div v-else-if="fallback === 'content'" class="mx-auto my-4 lg:flex lg:items-center space-y-8 lg:space-x-8 lg:space-y-0 rtl:space-x-reverse">
          <div class="h-48 w-full flex items-center justify-center rounded-sm bg-gray-300 sm:w-96 dark:bg-gray-700">
            <svg class="h-10 w-10 text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 18">
              <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z"/>
            </svg>
          </div>
          <div class="w-full">
            <div class="mb-4 h-2.5 w-48 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[80%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[60%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[70%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[50%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="h-2 max-w-[40%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
        <div v-else-if="fallback === 'swiper'" class="mx-auto max-w-[50%] flex-col gap-4 lg:flex lg:items-center space-y-8 lg:space-x-8 lg:space-y-0">
          <div class="h-80 w-full flex items-center justify-center rounded-sm bg-gray-300 dark:bg-gray-700">
            <svg class="h-20 w-full text-gray-200 dark:text-gray-600" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 18">
              <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z"/>
            </svg>
          </div>
          <div class="w-full">
            <div class="mb-2.5 h-2 max-w-[40%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[80%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
        <button
          v-else-if="fallback === 'button'"
          type="button"
          title="loading"
          class="w-20 items-center justify-center border border-gray-200 rounded-sm px-5 py-2.5 text-sm dark:border-gray-600 dark:hover:text-white dark:focus:ring-gray-700"
        >
          <div class="mx-auto h-1 max-w-[90%] rounded-full bg-white dark:bg-gray-700"></div>
        </button>
        <div
          v-else-if="fallback === 'form' || fallback === 'litepicker'"
          class="w-full items-center justify-center border border-gray-200 rounded-sm px-5 py-2.5 text-sm dark:border-gray-600 dark:hover:text-white dark:focus:ring-gray-700"
        >
          <div class="mx-auto h-1 max-w-[90%] rounded-full bg-white dark:bg-gray-700"></div>
        </div>

        <div v-else-if="fallback === 'footer'" class="lg:flex lg:items-center lg:gap-4">
          <div class="w-full max-lg:mb-4">
            <div class="mb-2.5 h-2 max-w-[75%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[60%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-[60%] rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
          <div class="relative w-full max-lg:hidden">
            <div class="mb-2.5 h-2 max-w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div class="mb-2.5 h-2 max-w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
        <template v-else>
          <div
            aria-hidden="true"
            class="mx-auto h-2 max-w-[75%] rounded-full bg-gray-200 dark:bg-gray-700"
          ></div>
        </template>
        <h2 v-if="fallback !== 'banner'" class="sr-only">
          Loading
        </h2>
      </section>
    </template>
    <slot></slot>
  </ClientOnly>
</template>

<style lang="scss" scoped>
.filter-50 {
  filter: opacity(0.5);
}
</style>
